import { Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { CreditAccount } from "@/models/creditCheck";
import {
  formatName,
  prettifyString,
  getStatusDescription,
  getPaymentFrequencyDescription,
  getStatusClass,
} from "@/services/CreditCheckService";

interface CreditCheckAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: CreditAccount | null;
}

function formatCurrency(val?: number | null): string {
  if (val == null || Number.isNaN(val)) return "-";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(val);
}

function formatDate(val?: string | null): string {
  if (!val) return "-";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function CreditCheckAccountDialog({
  open,
  onOpenChange,
  account,
}: CreditCheckAccountDialogProps) {
  if (!account) return null;

  const currentStatus = account.paymentHistory?.[0]?.status ?? null;
  const statusDesc = getStatusDescription(currentStatus);
  const statusClass = getStatusClass(currentStatus);

  const detailRows: Array<{
    label: string;
    value: React.ReactNode;
    tooltip?: string;
  }> = [
    { label: "Name", value: formatName(account.person) || "-" },
    { label: "Date of Birth", value: formatDate(account.person?.dateOfBirth) },
    {
      label: "Match Description",
      value: prettifyString(account.person?.accountHolderType) || "-",
    },
    {
      label: "Status",
      value: (
        <span
          className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold border ${statusClass}`}
        >
          {statusDesc}
        </span>
      ),
    },
    { label: "Company Type", value: prettifyString(account.companyType) || "-" },
    { label: "Account Type", value: prettifyString(account.accountType) || "-" },
    { label: "Company Name", value: account.companyName || "-" },
    { label: "Start Date", value: formatDate(account.startDate) },
    { label: "End Date", value: formatDate(account.endDate) },
    { label: "Last Updated", value: formatDate(account.lastUpdateDate) },
    { label: "Credit Limit", value: formatCurrency(account.creditLimit) },
    { label: "Credit Balance", value: formatCurrency(account.currentBalance) },
    { label: "Start Balance", value: formatCurrency(account.startBalance) },
    { label: "Default Balance", value: formatCurrency(account.defaultBalance) },
    { label: "Default Date", value: formatDate(account.defaultDate) },
    {
      label: "Last Three Plus Date",
      value: formatDate(account.lastThreePlusDate),
      tooltip:
        "3 plus are payments in arrears from 3, 4, 5, 6 and for Bad-Arrears, Repossession and Default. If the default is recursive in the payment history, then the earliest date is used. If a default date exists on TransUnion then this will be the 3 Plus date. If default date exists on Equifax then 3 Plus Date will be the default date if this date is most recent.",
    },
    { label: "Number of Payments", value: account.numberOfPayments ?? "-" },
    {
      label: "Payment Frequency",
      value: getPaymentFrequencyDescription(account.paymentFrequency),
    },
    { label: "Repayment Amount", value: formatCurrency(account.paymentAmount) },
    {
      label: "Supplementary Information",
      value:
        account.supplementaryInformation && account.supplementaryInformation.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {account.supplementaryInformation.map((info, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {info}
              </Badge>
            ))}
          </div>
        ) : (
          "-"
        ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="text-base font-semibold">
            Account Details — {account.companyName || prettifyString(account.accountType)}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Detailed breakdown of the credit account
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto px-6 py-4 flex-1">
          <div className="divide-y divide-border rounded-lg border border-border bg-card">
            {detailRows.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-2 gap-4 px-4 py-2.5 text-sm items-center hover:bg-muted/40 transition-colors"
              >
                <div className="flex items-center gap-1.5 font-medium text-muted-foreground">
                  <span>{row.label}</span>
                  {row.tooltip && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            tabIndex={0}
                            aria-label={`Info: ${row.label}`}
                            className="inline-flex cursor-pointer text-muted-foreground hover:text-foreground focus:outline-none"
                          >
                            <Info className="size-3.5" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs text-xs">
                          {row.tooltip}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <div className="text-foreground font-medium text-right">{row.value}</div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
